import {Component, inject, ViewChild} from '@angular/core';
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {EditChartEvent} from "../../../events/EditChartEvent";
import {ReloadDataEvent} from "../../../events/ReloadDataEvent";
import {CreateMetricGroupEvent} from "../../../events/CreateMetricGroupEvent";
import {UserMetricGroup} from "../../../models/user/Metric/UserMetricGroup";
import {BaseComponent} from "../../BaseComponent";
import {TypeOfDateSettingsEnumCharts} from "../../../enums/dashboard/TypeOfDateSettingsEnumCharts";
import {DatePipe} from "@angular/common";
import {PutUserMetricGroupCB, TimeIntervalDTO, UserMetricDTO} from "../../../ope/user/metric/PutUserMetricGroupCB";
import {UserOpeService} from "../../../services/ope/userOpe.service";
import JSZip from 'jszip';
import {ChartTimeSelectorComponent} from "./chart-time-selector/chart-time-selector.component";
import {TelemetryOpeService} from "../../../services/ope/telemetryOpe.service";
import {DownloadTimeChartMetricsCB} from "../../../ope/telemetry/DownloadTimeChartMetricsCB";
import {saveAs} from "file-saver";
import {GetTimeChartMetricsCB} from "../../../ope/telemetry/GetTimeChartMetricsCB";
import {TimeUnitEnum} from "../../../enums/dashboard/TimeUnitEnum";

@Component({
  selector: 'app-chart-controls',
  templateUrl: './chart-controls.component.html',
  styleUrls: ['./chart-controls.component.css', '../../../styles/mat-icons-wrapper.css']
})
export class ChartControlsComponent extends BaseComponent {
  @ViewChild('chartTimeSelectorComponent') chartTimeSelectorComponent?: ChartTimeSelectorComponent

  /**
   * Flag controlling the visibility of the popover, true is shown false is hidden
   * @protected
   */
  protected datesPopoverVisible = false;

  /**
   * Boolean flag used to store the validity of the form
   * @protected
   */
  protected bucketIsValid: boolean = true;

  /**
   * Boolean flag used to store the validity of the form
   * @protected
   */
  protected dateIsValid: boolean = true;

  /**
   * Wrapper variable containing TypeOfDateSettingsEnumCharts enum, used by the HTML
   * @protected
   */
  protected readonly TypeOfDateSettingsEnum = TypeOfDateSettingsEnumCharts;

  /**
   * Variable containing a deep copy of the selected user metric group used for form data
   * @protected
   */
  protected copiedUserMetricGroup!: UserMetricGroup | null;


  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService: DashboardService = inject(DashboardService);

  protected telemetryOpeService: TelemetryOpeService = inject(TelemetryOpeService);

  /**
   * Service used for api calls related to users
   * @protected
   */
  protected userService: UserOpeService = inject(UserOpeService);

  /**
   * Pipe used to translate timestamps in string dates
   * @protected
   */
  protected datePipe: DatePipe = inject(DatePipe);

  /**
   * Flag used to block false positive reload requests
   * @protected
   */
  protected realReload = false;

  /**
   * A series of events call
   */
  editChart() {
    this.eventBus.emitEvent(new EditChartEvent());
  }

  reloadChart() {
    if (this.realReload)
      this.eventBus.emitEvent(new ReloadDataEvent());
    this.realReload = false;
  }

  createMetricGroup(): void {
    this.eventBus.emitEvent(new CreateMetricGroupEvent());
  }

  exportChart() {
    //this.eventBus.emitEvent(new ExportChartEvent('csv'))
    if (this.dateIsValid &&
      this.copiedUserMetricGroup &&
      this.dashboardService.fullSelUserMetricGroup) {
      const cb = new DownloadTimeChartMetricsCB();

      cb.i.groupId = this.dashboardService.fullSelUserMetricGroup?.id;

      this.mapExportDatesInputForAPICall(cb);
      this.datesPopoverVisible = false;

      this.telemetryOpeService.downloadTimeChartMetrics(cb)
        .then(_ => {
          const file = cb.o.bc;
          const regainedFile = new Blob([Uint8Array.from(atob(file.ba), c => c.charCodeAt(0))], {type: file.mimeType});
          const zip = new JSZip();
          zip.loadAsync(regainedFile)
            .then(unzippedFile => {
              if (Object.keys(unzippedFile.files).length > 0) {
                const innerFileName = Object.keys(unzippedFile.files)[0];
                const innerExtension = innerFileName.includes('.') ? innerFileName.substring(innerFileName.lastIndexOf('.')) : '';
                zip.files[innerFileName].async('blob')
                  .then((blob: Blob) => {
                    saveAs(blob, file.fileName + innerExtension);
                  })
              } else {
                return;
              }
            })
        })
    }
  }

  /**
   * Opens the date selector popover
   */
  openDatesSelector() {
    this.datesPopoverVisible = !this.datesPopoverVisible;
  }


  /**
   * Saves the new date ranges and reloads the chart
   */
  saveNewDatesRangeAndReloadData() {
    if (this.bucketIsValid && this.dateIsValid) {
      if (!!this.dashboardService.fullSelUserMetricGroup && !!this.copiedUserMetricGroup) {
        let cb = new PutUserMetricGroupCB();
        cb.i.id = this.dashboardService.fullSelUserMetricGroup.id;
        cb.i.name = this.dashboardService.fullSelUserMetricGroup.name;
        cb.i.bucketUnits = this.copiedUserMetricGroup.bucketUnits;
        cb.i.bucketValue = this.copiedUserMetricGroup.bucketValue;
        cb.i.metrics = [];
        this.dashboardService.fullSelUserMetricGroup.metrics?.forEach(
          metric => {
            if (!!metric.chartType && !!metric.functionType) {
              let newMetric = new UserMetricDTO();
              newMetric.id = metric.id;
              newMetric.color = metric.color;
              newMetric.chartType = metric.chartType;
              newMetric.functionType = metric.functionType;
              cb.i.metrics!.push(newMetric);
            }
          }
        );
        cb.i.timeInterval = new TimeIntervalDTO();
        cb.i.timeInterval.startDate = this.copiedUserMetricGroup.startDate;
        cb.i.timeInterval.endDate = this.copiedUserMetricGroup.endDate;
        cb.i.timeInterval.selectedDateSettings = this.copiedUserMetricGroup.selectedDateSettings;
        cb.i.timeInterval.realTimeUnit = this.copiedUserMetricGroup.realTimeUnit;
        cb.i.timeInterval.realtimeValue = this.copiedUserMetricGroup.realtimeValue;
        this.userService.putUserMetricGroup(cb).then(() => {
          this.realReload = true;
          this.reloadChart();
          this.datesPopoverVisible = false
        });
      }
    }
  }

  /**
   * Beautyfies and prints the dates in the correct format
   */
  printDateSelectionText() {
    if (!!this.dashboardService.fullSelUserMetricGroup) {
      if (this.dashboardService.fullSelUserMetricGroup.selectedDateSettings === TypeOfDateSettingsEnumCharts.FIXED_DATE) {
        return `<span>${this.datePipe.transform(this.dashboardService.fullSelUserMetricGroup.startDate, 'dd/MM/yyyy HH:mm')}</span><span>${this.datePipe.transform(this.dashboardService.fullSelUserMetricGroup.endDate, 'dd/MM/yyyy HH:mm')}</span>`;
      } else if (this.dashboardService.fullSelUserMetricGroup.selectedDateSettings === TypeOfDateSettingsEnumCharts.REALTIME) {
        let translation = this.dashboardService.fullSelUserMetricGroup.realtimeValue === 1 ? 'DATE_SELECTION.REALTIME.SINGULAR.' : 'DATE_SELECTION.REALTIME.PLURAL.';
        return `<span>${this.translate.instant('DATE_SELECTION.REALTIME.TITLE')}:</span><span>${this.dashboardService.fullSelUserMetricGroup.realtimeValue + ' ' + this.translate.instant(translation + this.dashboardService.fullSelUserMetricGroup.realTimeUnit)}</span>`;
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  checkDateRange() {
    if (!!this.chartTimeSelectorComponent)
      this.dateIsValid = this.chartTimeSelectorComponent.checkStartDateValidity();
  }

  mapExportDatesInputForAPICall(cb: DownloadTimeChartMetricsCB) {
    if (!!this.copiedUserMetricGroup) {

      if (this.copiedUserMetricGroup.selectedDateSettings === TypeOfDateSettingsEnumCharts.FIXED_DATE) {
        if (!!this.copiedUserMetricGroup.endDate) {
          cb.i.dateTo = new Date(this.copiedUserMetricGroup.endDate).getTime();
        }
        if (!!this.copiedUserMetricGroup.startDate) {
          cb.i.dateFrom = new Date(this.copiedUserMetricGroup.startDate).getTime();
        }
      } else if (this.copiedUserMetricGroup.selectedDateSettings === TypeOfDateSettingsEnumCharts.REALTIME) {
        let now = new Date();
        cb.i.dateTo = now.getTime();
        switch (this.copiedUserMetricGroup.realTimeUnit) {
          case TimeUnitEnum.MINUTES:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes() - this.copiedUserMetricGroup.realtimeValue, now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.HOURS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() - this.copiedUserMetricGroup.realtimeValue, now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.DAYS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate() - this.copiedUserMetricGroup.realtimeValue, now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.WEEKS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate() - this.copiedUserMetricGroup.realtimeValue * 7, now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.MONTHS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth() - this.copiedUserMetricGroup.realtimeValue, now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.YEARS:
            cb.i.dateFrom = new Date(now.getFullYear() - this.copiedUserMetricGroup.realtimeValue, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
        }
      }

      if (!!this.copiedUserMetricGroup!.bucketValue)
        cb.i.bucketValue = this.copiedUserMetricGroup!.bucketValue;
      if (!!this.copiedUserMetricGroup!.bucketUnits)
        cb.i.bucketUnits = this.copiedUserMetricGroup!.bucketUnits;

      if (cb.i.dateFrom === cb.i.dateTo) {
        cb.i.dateTo++;
      }
    }
  }
}
